import { useEffect, useState } from "react";

import { useBackend } from "../app/BackendProvider";
import { describeError, useResource } from "../app/useResource";
import { formatAgo, formatDeviationShort, formatIsoTime } from "../app/format";
import { useNetworkId } from "../config/ConfigProvider";
import { useActivePostParam } from "../posts/PostProvider";
import type { AnticipationEvent, Journey, RegulationOptionsResponse } from "../contract/types";
import { usePolledResource } from "./hooks";
import { LineBadge } from "./bits";
import type { ToolsScope } from "./toolsScope";

const KIND_LABEL: Record<AnticipationEvent["kind"], string> = {
  "gap-forming": "Trou de desserte",
  "bunching-forming": "Paquet en formation",
  "delay-cascade": "Cascade de retard",
};

const FEASIBILITY_LABEL: Record<string, string> = {
  feasible: "réalisable",
  uncertain: "incertain",
  infeasible: "non réalisable",
};

/**
 * Panneau « Aide à la décision » (contrat v1.44, remplace « Retournement ») —
 * le moteur d'anticipation balaie le réseau et remonte les situations qui vont
 * dégénérer AVANT qu'elles ne le fassent, avec une action de rattrapage
 * chiffrée quand elle existe. Consultatif : aucun bouton « Appliquer », aucun
 * acquittement — un événement disparaît de lui-même quand la situation se
 * résorbe. Rafraîchi par polling (~20 s, dans la fourchette 15-30 s du brief).
 */
export function AnticipationPanel({
  scope,
  onSelectJourney,
  onCount,
}: {
  /** Réseau entier : tout ce que le moteur propose. Véhicule : uniquement ce qui le concerne. */
  scope: ToolsScope;
  onSelectJourney: (id: string) => void;
  onCount?: (n: number) => void;
}) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const postParam = useActivePostParam();

  const { data, error, loading } = usePolledResource(
    (signal) => backend.api.getAnticipation(networkId, postParam ? { post: postParam } : {}, signal),
    [backend, networkId, postParam],
    20_000,
  );
  const events = data?.events ?? [];
  // Badge de l'onglet : toujours l'alerte RÉSEAU (événements priorité haute),
  // quelle que soit la portée affichée — un véhicule ouvert ne doit pas masquer
  // qu'ailleurs sur le réseau quelque chose dégénère.
  const highCount = events.filter((e) => e.priority === "high").length;

  useEffect(() => {
    onCount?.(highCount);
  }, [onCount, highCount]);

  if (loading && !data) return <p className="has-text-grey is-size-7">Chargement…</p>;
  if (error) return <p className="has-text-danger is-size-7">{describeError(error)}</p>;

  if (scope.kind === "vehicle") {
    const own = events.filter((e) => e.journeyId === scope.journey.id);
    return <VehicleMeasures journey={scope.journey} events={own} onSelectJourney={onSelectJourney} />;
  }

  if (events.length === 0) return <p className="has-text-grey is-size-7">Rien à signaler sur le réseau.</p>;

  return (
    <div style={{ display: "grid", gap: 8 }}>
      {events.map((e) => (
        <EventCard key={e.id} event={e} onSelectJourney={onSelectJourney} />
      ))}
    </div>
  );
}

/**
 * Portée « véhicule » : les situations que le moteur a détectées sur CE véhicule
 * (dépliées d'emblée, avec leurs manœuvres) ; à défaut, les manœuvres possibles
 * pour lui, demandées directement — la régulation d'un véhicule ne dépend pas
 * de l'existence d'une alerte réseau.
 */
function VehicleMeasures({
  journey,
  events,
  onSelectJourney,
}: {
  journey: Journey;
  events: AnticipationEvent[];
  onSelectJourney: (id: string) => void;
}) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const journeyId = journey.id;
  // Sans événement, on va chercher les manœuvres pour la course elle-même.
  const needOptions = events.length === 0;
  const options = useResource(
    (signal) =>
      needOptions ? backend.api.getRegulationOptions(networkId, journeyId, signal) : Promise.resolve(null),
    [backend, networkId, journeyId, needOptions],
  );

  if (events.length > 0) {
    return (
      <div style={{ display: "grid", gap: 8 }}>
        {events.map((e) => (
          <EventCard key={e.id} event={e} onSelectJourney={onSelectJourney} defaultExpanded />
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 6 }}>
      <p className="has-text-grey is-size-7" style={{ margin: 0 }}>
        Le moteur ne signale aucune situation à traiter pour ce véhicule.
      </p>
      {options.loading && !options.data ? (
        <p className="has-text-grey is-size-7">Chargement des manœuvres…</p>
      ) : options.error ? (
        <p className="has-text-grey is-size-7">Manœuvres indisponibles pour cette course.</p>
      ) : options.data && options.data.options.length > 0 ? (
        <>
          <p className="ovl" style={{ margin: "4px 0 0" }}>
            Manœuvres possibles
          </p>
          <OptionsList options={options.data.options} />
        </>
      ) : null}
    </div>
  );
}

function OptionsList({ options }: { options: RegulationOptionsResponse["options"] }) {
  return (
    <ul style={{ display: "grid", gap: 4, listStyle: "none", padding: 0, margin: 0 }}>
      {options.map((o, i) => (
        <li key={i} className="is-size-7">
          <strong>{o.label}</strong> —{" "}
          <span
            style={{
              color:
                o.feasibility === "feasible" ? "#15803d" : o.feasibility === "uncertain" ? "#b7950b" : "var(--sentinelle-ink-3)",
            }}
          >
            {FEASIBILITY_LABEL[o.feasibility]}
          </span>
          {o.detail ? ` · ${o.detail}` : ""}
        </li>
      ))}
    </ul>
  );
}

function EventCard({
  event,
  onSelectJourney,
  defaultExpanded = false,
}: {
  event: AnticipationEvent;
  onSelectJourney: (id: string) => void;
  /** Déplie d'emblée (portée véhicule : les manœuvres sont la raison d'être de l'écran). */
  defaultExpanded?: boolean;
}) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [detail, setDetail] = useState<RegulationOptionsResponse | null>(null);
  const [detailError, setDetailError] = useState<unknown>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const now = Date.now();

  async function loadDetail() {
    if (detail || !event.journeyId) return;
    setDetailLoading(true);
    setDetailError(null);
    try {
      setDetail(await backend.api.getRegulationOptions(networkId, event.journeyId));
    } catch (err) {
      setDetailError(err);
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    if (defaultExpanded) void loadDetail();
    // Chargement à l'apparition de la carte uniquement (la course pivot ne change pas
    // pour un `event.id` donné).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggle() {
    if (event.journeyId) onSelectJourney(event.journeyId);
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    await loadDetail();
  }

  const best = event.plan?.best ?? null;

  return (
    <div
      style={{
        border: "1px solid var(--sentinelle-rule)",
        borderLeft: `3px solid ${event.priority === "high" ? "var(--sentinelle-signal)" : "var(--sentinelle-rule)"}`,
        background: "#fff",
        borderRadius: 4,
        padding: "9px 12px",
      }}
    >
      <button
        type="button"
        onClick={toggle}
        style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: 0, cursor: "pointer" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <LineBadge lineId={event.lineId} size={20} />
          <span
            className="ovl"
            style={{ color: event.priority === "high" ? "var(--sentinelle-signal)" : "var(--sentinelle-ink-3)", fontWeight: 700 }}
          >
            {KIND_LABEL[event.kind]}
          </span>
          <span className="has-text-grey is-size-7" style={{ marginInlineStart: "auto" }}>
            depuis {formatAgo(event.since, now)}
          </span>
        </div>
        <p style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--sentinelle-ink)", margin: 0 }}>{event.headline}</p>
        <p className="has-text-grey is-size-7" style={{ margin: "2px 0 0" }}>
          {event.detail}
          {event.atStopName ? ` · ${event.atStopName}` : ""}
        </p>

        {best ? (
          <p className="is-size-7" style={{ margin: "6px 0 0" }}>
            {best.pattern === "turnback" ? "Retournement à" : "Terminus puis HLP jusqu'à"}{" "}
            <strong>{best.atStopName}</strong>
            {best.catchesDepartureAt ? (
              <>
                {" "}
                → rejoint le départ de <strong>{formatIsoTime(best.catchesDepartureAt)}</strong>
              </>
            ) : null}
            {best.resultingDeviationSeconds != null ? (
              <>
                {" "}
                → écart ramené à <strong>{formatDeviationShort(best.resultingDeviationSeconds)}</strong>
                {best.recoverySeconds != null ? ` (${Math.round(best.recoverySeconds / 60)} min rattrapées)` : ""}
              </>
            ) : null}
            {best.stopsNotServedCount > 0
              ? ` · ${best.stopsNotServedCount} arrêt${best.stopsNotServedCount > 1 ? "s" : ""} non desservi${best.stopsNotServedCount > 1 ? "s" : ""}`
              : ""}
            {best.outcome === "partial" ? " · le véhicule attendrait" : ""}
          </p>
        ) : (
          <p className="has-text-grey is-size-7" style={{ margin: "6px 0 0" }}>
            Pas de plan chiffré · <span style={{ textDecoration: "underline" }}>Détail</span>
          </p>
        )}
      </button>

      {expanded ? (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--sentinelle-rule)" }}>
          {detailLoading ? (
            <p className="has-text-grey is-size-7">Chargement des manœuvres…</p>
          ) : detailError ? (
            <p className="has-text-danger is-size-7">{describeError(detailError)}</p>
          ) : detail ? (
            <OptionsList options={detail.options} />
          ) : (
            <p className="has-text-grey is-size-7">Course non identifiée — pas de manœuvre chiffrable.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
